import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FocusedEditDeclined } from 'layout/MainLayout/focusedEdit';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetExpenseByIdQuery } from 'store/api/expenses';
import { ExpenseForm } from 'types/expenses';
import { useClassifiers } from 'utils/useClassifiers';
import { EXPENSE_FORM_CLASSIFIERS, expenseDtoToForm } from './expenseFormMapping';
import ExpensePageForm from './ExpensePageForm';

const ClassicExpensePageEdit = (): JSX.Element => {
    const { id: expenseId } = useParams();

    const { data: expense, isLoading, isFetching, refetch } = useGetExpenseByIdQuery(expenseId ?? skipToken);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EXPENSE_FORM_CLASSIFIERS });

    const initialData = useMemo<ExpenseForm | undefined>(
        () => (expense ? expenseDtoToForm(expense, classifiers) : undefined),
        [expense, classifiers]
    );

    if (isLoading || isFetching || isLoadingClassifiers) {
        return <Loader />;
    }

    // A failed fetch (after the global handler has redirected 404/403) must not fall through to the
    // form, which would otherwise render as a blank create flow on /expense/:id/edit.
    if (!expense || initialData === undefined) {
        return <FocusedEditDeclined />;
    }

    return (
        <ExpensePageForm
            initialData={initialData}
            isDeletable={expense.isDeletable}
            isEditable={expense.isEditable}
            refetch={() => {
                void refetch();
            }}
        />
    );
};

/** In the new design an expense is edited section by section on its page, so its edit URL opens Expense there. */
const ExpensePageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: expenseId } = useParams();
    return isNew ? <Navigate to={`/expense/${expenseId}?edit=expense`} replace /> : <ClassicExpensePageEdit />;
};

export default ExpensePageEdit;
